import * as React from "react";
import { GridColumns } from "@mui/x-data-grid-pro";
import { useSelector } from "react-redux";
import CclPersistentDataGrid from "../../components/common/cclPersistentDataGrid/cclPersistentDataGrid";
import { addCustomDataGridFilters } from "../../components/common/customDataGridFilters/customDataGridFilters";
import { DocumentItem } from "../../services/types/assessmentAPITypes";
import { updatedocumentsGridState } from "../../app/slices/documentListTableStateSlice";
import { eGroupIds, UserClaimsService } from "../../services/currentUserService/currentUserService";

export interface DocumentsListProps {
    documents: DocumentItem[];
    loading: boolean;
    uploadDocument: () => void;
    editDocument: (document?: DocumentItem) => void;
    downloadDocument: (document?: DocumentItem) => void;
    downloadInProgress: boolean;
    enableDisableDocument: (document?: DocumentItem) => void;
    enableDisableInProgress: boolean;
    deleteDocument: (document?: DocumentItem) => void;
    deleteInProgress: boolean;
}

export const DocumentsDataGridPro: React.FC<DocumentsListProps> = (props) => {
    const stateSelector = useSelector((state: any) => state.documentListTableState);
    const isSuperAdmin = new UserClaimsService().IsUserInGroups([eGroupIds.eCCLSuperAdmin]);

    const [showEnabledDocuments, setShowEnabledDocuments] = React.useState<boolean>(false);
    const [selectedRow, setSelectedRow] = React.useState<DocumentItem[] | null>(null);

    let COLUMNS = [
        {
            field: "documentId",
            headerName: "ID",
            type: "string",
            flex: 1,
        },
        {
            field: "documentName",
            headerName: "Title",
            type: "string",
            flex: 1,
        },
        {
            field: "active",
            headerName: "Enabled",
            type: "boolean",
            renderCell: (params: any) => (params.value ? "Yes" : "No"),
            flex: 1,
        },
        {
            field: "uploadedBy",
            headerName: "Uploaded By",
            type: "string",
            flex: 1,
        },
        {
            field: "uploadedDate",
            headerName: "Uploaded",
            valueGetter: (params: any) => (params.value ? new Date(params.value) : null),
            type: "dateTime",
            renderCell: (params: any) => {
                return params.value ? new Date(params.value).toLocaleDateString() : null;
            },
            flex: 1,
        },
        {
            field: "modifiedBy",
            headerName: "Modified By",
            type: "string",
            flex: 1,
        },
        {
            field: "modifiedDate",
            headerName: "Modified",
            valueGetter: (params: any) => (params.value ? new Date(params.value) : null),
            type: "dateTime",
            renderCell: (params: any) => {
                return params.value ? new Date(params.value).toLocaleDateString() : null;
            },
            flex: 1,
        },
    ];

    let BUTTONS = [
        {
            id: "upload-document-button",
            caption: "Upload Document",
            onClick: props.uploadDocument,
            returnSelected: true,
            restricted: true,
        },
        {
            id: "edit-document-button",
            caption: "Edit Document",
            onClick: () => props.editDocument(selectedRow?.[0] ?? undefined),
            disable: !selectedRow || selectedRow.length !== 1,
            returnSelected: true,
            restricted: true,
        },
        {
            id: "download-document-button",
            caption: "Download Document",
            onClick: () => props.downloadDocument(selectedRow?.[0] ?? undefined),
            disable: !selectedRow || selectedRow.length > 1 || selectedRow.length === 0,
            returnSelected: true,
            restricted: true,
            loadingBtn: true,
            isLoading: props.downloadInProgress,
        },
        {
            id: "enable-disable-document-button",
            caption: `${selectedRow?.[0]?.active ? "Disable" : "Enable"} Document`,
            onClick: () => props.enableDisableDocument(selectedRow?.[0] ?? undefined),
            disable: !selectedRow || selectedRow.length > 1 || selectedRow.length === 0,
            returnSelected: true,
            restricted: true,
            loadingBtn: true,
            isLoading: props.enableDisableInProgress,
        },
        {
            id: "delete-document-button",
            caption: "Delete Document",
            onClick: () => props.deleteDocument(selectedRow?.[0] ?? undefined),
            disable: !selectedRow || selectedRow.length !== 1,
            returnSelected: true,
            restricted: true,
            loadingBtn: true,
            isLoading: props.deleteInProgress,
            hidden: !isSuperAdmin,
        },
        {
            id: "show-enabled-documents-switch",
            caption: "Show Only Enabled",
            checkedState: showEnabledDocuments,
            onToggled: (checked: boolean) => {
                setShowEnabledDocuments(checked);
            },
            onClick: () => {},
            type: "switch",
            returnSelected: false,
            restricted: true,
        },
    ];

    const enabledGridModel = React.useMemo(
        () =>
            showEnabledDocuments
                ? {
                      items: [
                          {
                              columnField: "active",
                              operatorValue: "is",
                              value: "true",
                          },
                      ],
                  }
                : undefined,
        [showEnabledDocuments],
    );

    const updateSelectedRows = (rows?: any[]) => {
        setSelectedRow(rows ?? null);
    };

    return (
        <CclPersistentDataGrid
            columns={addCustomDataGridFilters(COLUMNS as GridColumns<any>)}
            data={props.documents ?? []}
            loading={props.loading}
            stateSelector={stateSelector}
            userPreferenceId={1}
            getRowIdFn={(row: DocumentItem) => row.documentId}
            actionCreator={updatedocumentsGridState}
            hideToolbar={false}
            resultBar={false}
            resultBarText={""}
            miscButtons={BUTTONS}
            hideFooter={false}
            filterModel={enabledGridModel}
            showCheckboxes={true}
            onSelectedRowsChange={updateSelectedRows}
            hideSettingsButtons={true}
        />
    );
};

export default DocumentsDataGridPro;
